/**
 * Custom hook for managing dish form state and operations
 */

import { useState, useEffect, useCallback } from 'react';
import { validateDishForm, isValidPrice } from '../utils/validation.js';
import { 
  initializeFormData, 
  cleanFormDataForSubmission, 
  getConfiguredGroupIds,
  processAdditionsData,
  formatAdditionOption,
} from '../utils/dishFormUtils.js';
import { TransformDataForBackend } from '../utils/dishUtils.js';
import { 
  createDish, 
  updateDish, 
  fetchDishAdditions, 
  fetchAdditionGroups,
  saveDishAddition
} from '../services/menu-service.js';
import { getTranslation } from '../utils/translate.js';
import { translateToRuAndEn, translateMultiple } from '../utils/googleTranslate.js';

// Module-level cache to prevent duplicate API calls across hook instances
const additionsCache = {
  additions: null,
  groups: null,
  isFetching: false
};

/**
 * Custom hook for dish form management
 * @param {Object} props - Hook properties
 * @param {Object} props.dishData - Initial dish data (if editing)
 * @param {string} props.chefId - Chef ID
 * @param {Function} props.onDishCreated - Callback after dish creation/update
 * @param {Object} props.toastRef - Reference to toast component
 * @param {string} props.language - Current language
 * @param {boolean} props.refreshAdditionsFlag - Flag to trigger additions refresh
 * @returns {Object} Form state and handlers
 */
const useDishForm = ({ 
  dishData, 
  chefId, 
  onDishCreated, 
  toastRef, 
  language,
  refreshAdditionsFlag 
}) => {
  // Form state
  const [formData, setFormData] = useState(initializeFormData(dishData, chefId));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [image, setImage] = useState(null);

  // Re-initialize form data when dishData changes (important for edit mode)
  useEffect(() => {
    if (dishData) {
      const initializedData = initializeFormData(dishData, chefId);
      setFormData(initializedData);
      setConfiguredGroupIds(getConfiguredGroupIds(dishData));
      // Set image URL if dish has an image
      if (dishData.url) {
        setImage(dishData.url);
      }
    }
  }, [dishData, chefId]);

  // Additions state
  const [allDishAdditions, setAllDishAdditions] = useState([]);
  const [additionGroups, setAdditionGroups] = useState([]);
  const [configuredGroupIds, setConfiguredGroupIds] = useState(() => 
    getConfiguredGroupIds(dishData)
  );

  // Process additions data
  const { ungroupedAdditions, groupedAdditionsMap } = processAdditionsData(allDishAdditions);

  // Format options for ungrouped additions select
  const ungroupedAdditionOptions = ungroupedAdditions.map(add => 
    formatAdditionOption(add, language)
  );

  // Get currently selected ungrouped additions for the select value
  const selectedUngroupedValues = formData.dishAdditionDtoList
    .filter(add => ungroupedAdditions.some(u => u.id === add.id))
    .map(add => formatAdditionOption(add, language));

  // Memoize available groups for the "Add Configuration" dropdown
  const availableGroupsForConfig = additionGroups
    .filter(group => !configuredGroupIds.includes(group.id))
    .map(group => ({
      value: group.id, 
      label: `${group.nameHy}`,
      group: group
    }));

  // Load additions and groups data
  useEffect(() => {
    const loadAdditionsData = async () => {
      // Use cached data if available - no need to fetch again
      if (additionsCache.additions && additionsCache.groups) {
        setAllDishAdditions(additionsCache.additions);
        setAdditionGroups(additionsCache.groups);
        return;
      }
      
      // Prevent concurrent fetches
      if (additionsCache.isFetching) return;
      additionsCache.isFetching = true;
      try {
        const [additions, groups] = await Promise.all([
          fetchDishAdditions(),
          fetchAdditionGroups()
        ]);

        additionsCache.additions = additions || [];
        additionsCache.groups = groups || [];
        setAllDishAdditions(additionsCache.additions);
        setAdditionGroups(additionsCache.groups);
      } catch (error) {
        console.error("Error loading additions data:", error);
        toastRef.current?.addToast(
          getTranslation('common.error', language) || 
          "Failed to load additions data", 
          "error"
        );
        additionsCache.additions = null; // Clear cache on error to allow retry
        additionsCache.groups = null;
      } finally {
        additionsCache.isFetching = false;
      }
    };

    loadAdditionsData();
    // Only run once on mount - don't depend on language/toastRef as cache handles it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate effect for refreshing additions based on the flag
  useEffect(() => {
    const refreshAdditions = async () => {
      // Only refresh if flag is explicitly true (not undefined or false)
      if (refreshAdditionsFlag !== true) return;
      if (additionsCache.isFetching) return;
      additionsCache.isFetching = true;

      try {
        const [additions, groups] = await Promise.all([
          fetchDishAdditions(),
          fetchAdditionGroups()
        ]);

        additionsCache.additions = additions || [];
        additionsCache.groups = groups || [];
        setAllDishAdditions(additionsCache.additions);
        setAdditionGroups(additionsCache.groups);

        // Update configured groups if needed (e.g., if a group was deleted)
        setConfiguredGroupIds(prevIds => 
          prevIds.filter(id => groups.some(g => g.id === id))
        );
      } catch (error) {
        console.error("Error refreshing dish additions/groups:", error);
        toastRef.current?.addToast(
          getTranslation('common.error', language) || 
          "Failed to refresh additions/groups", 
          "error"
        );
        additionsCache.additions = null; // Clear cache on error
        additionsCache.groups = null;
      } finally {
        additionsCache.isFetching = false;
      }
    };

    refreshAdditions();
  }, [refreshAdditionsFlag, language, toastRef]);

  // Input change handler
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    // For price fields, only allow digits (0-9)
    if (name === "price" && !isValidPrice(value)) {
      return;
    }

    // Clear error if value is entered
    if (value && errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    setFormData(prevData => ({
      ...prevData,
      [name]: (name === "price" || name === "expirationDate" || name === "orderBefore" || name === "minimumOrderCount")
        ? (value === "" ? "" : parseFloat(value))
        : value,
    }));
  }, [errors]);

  // Single select change handler
  const handleSingleSelectChange = useCallback((selectedOption, field) => {
    let valueToSet = null;

    if (selectedOption) {
      switch (field) {
        case "type":
        case "spiceLevel":
          valueToSet = selectedOption.value;
          break;
        case "dishCuisineTagDto":
          valueToSet = selectedOption.value ? {
            id: selectedOption.value,
            cuisineTag: selectedOption.label,
            translations: selectedOption.translations
          } : null;
          break;
        case "dishTagDto":
          valueToSet = selectedOption.value ? {
            id: selectedOption.value,
            tag: selectedOption.label,
            translations: selectedOption.translations
          } : null;
          break;
        default:
          valueToSet = selectedOption;
      }
    }

    setFormData(prevData => ({
      ...prevData,
      [field]: valueToSet
    }));

    // Clear error if a selection is made
    if (selectedOption && errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  // Multi-select change handler
  const handleMultiSelectChange = useCallback((selectedOptions, field) => {
    let valueToSet = [];

    if (selectedOptions && selectedOptions.length > 0) {
      valueToSet = selectedOptions.map(option => {
        switch (field) {
          case "adjustableSpiceLevelDtoList":
            return { id: option.value, spiceLevel: option.label };
          case "dietaryOptionDtoList":
            return { 
              id: option.id, 
              dietaryOptionValue: option.dietaryOptionValue 
            };
          default:
            return option;
        }
      }).filter(Boolean);
    }

    setFormData(prevData => ({
      ...prevData,
      [field]: valueToSet
    }));

    // Clear error if a selection is made
    if (selectedOptions?.length > 0 && errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  // Ungrouped additions change handler
  const handleUngroupedAdditionsChange = useCallback((selectedOptions) => {
    const newlySelectedIds = new Set(selectedOptions ? selectedOptions.map(opt => opt.value) : []);
    const previouslySelectedIds = new Set(selectedUngroupedValues.map(a => a.value));

    setFormData(prev => {
      const groupedAdditions = prev.dishAdditionDtoList.filter(add => add.group && add.group.id);
      let newUngrouped = [];

      // Keep existing selected ones that are still selected
      prev.dishAdditionDtoList.forEach(add => {
        if (!add.group && newlySelectedIds.has(add.id)) {
          newUngrouped.push(add); // Keep existing with its override price
        }
      });

      // Add newly selected ones (that weren't previously selected)
      newlySelectedIds.forEach(id => {
        if (!previouslySelectedIds.has(id)) {
          const fullAddition = ungroupedAdditions.find(a => a.id === id);
          if (fullAddition) {
            const basePrice = typeof fullAddition.price === 'number' ? fullAddition.price : 0;
            newUngrouped.push({...fullAddition, price: basePrice, overridedPrice: null});
          }
        }
      });

      return {
        ...prev, 
        dishAdditionDtoList: [...groupedAdditions, ...newUngrouped]
      };
    });
  }, [ungroupedAdditions, selectedUngroupedValues]);

  // Grouped addition change handler
  const handleGroupedAdditionChange = useCallback((selectedOptions, group) => {
    const groupId = group.id;
    const newlySelectedFullAdditions = selectedOptions 
      ? selectedOptions.map(option => option?.addition).filter(Boolean) 
      : [];
    const newlySelectedIds = new Set(newlySelectedFullAdditions.map(a => a.id));

    setFormData(prev => {
      // Get additions NOT in the current group
      const otherAdditions = prev.dishAdditionDtoList.filter(add => add.group?.id !== groupId);

      // Filter the previous selections from THIS group to see which are kept
      const previouslySelectedInGroup = prev.dishAdditionDtoList.filter(add => add.group?.id === groupId);

      let newSelectionsForThisGroup = [];

      // Keep existing selections if they are still selected (preserve override price)
      previouslySelectedInGroup.forEach(prevAdd => {
        if (newlySelectedIds.has(prevAdd.id)) {
          newSelectionsForThisGroup.push(prevAdd);
        }
      });

      // Add brand new selections (initialize override price)
      newlySelectedFullAdditions.forEach(newAdd => {
        const alreadyExists = newSelectionsForThisGroup.some(p => p.id === newAdd.id);
        if (!alreadyExists) {
          const basePrice = typeof newAdd.price === 'number' ? newAdd.price : 0;
          newSelectionsForThisGroup.push({
            ...newAdd, 
            price: basePrice, 
            overridedPrice: null, 
            group: group
          });
        }
      });

      return {
        ...prev, 
        dishAdditionDtoList: [...otherAdditions, ...newSelectionsForThisGroup]
      };
    });

    const errorKey = `additionGroup_${groupId}`;
    if (errors[errorKey] && newlySelectedFullAdditions.length > 0) {
      setErrors(prevErrs => ({...prevErrs, [errorKey]: undefined}));
    }
  }, [errors]);

  // Addition price override change handler
  const handleAdditionPriceOverrideChange = useCallback((additionId, newPrice) => {
    // Only allow digits (0-9)
    if (!isValidPrice(newPrice)) {
      return;
    }

    const priceValue = newPrice === '' ? 0 : parseFloat(newPrice);

    setFormData(prev => ({
      ...prev,
      dishAdditionDtoList: prev.dishAdditionDtoList.map(add =>
        add.id === additionId ? {...add, overridedPrice: priceValue} : add
      )
    }));
  }, []);

  // Ingredient translation change handler
  const handleTranslationChange = useCallback((value, ingredientKey = 'ingredientHy') => {
    setFormData(prevData => ({
      ...prevData,
      ingridientsDto: [{
        ...prevData.ingridientsDto[0] || {}, 
        [ingredientKey]: value
      }]
    }));

    if (value && errors[ingredientKey]) {
      setErrors(prev => ({...prev, [ingredientKey]: undefined}));
    }
  }, [errors]);

  // Translation state
  const [isTranslating, setIsTranslating] = useState(false);

  // Translate ingredients handler
  const handleTranslateIngredients = useCallback(async () => {
    const ingredientHy = formData.ingridientsDto[0]?.ingredientHy;
    if (!ingredientHy || !ingredientHy.trim()) {
      toastRef.current?.addToast(
        getTranslation('toast.enterArmenianFirst', language) || "Please enter Armenian ingredients first",
        "warning"
      );
      return;
    }

    setIsTranslating(true);
    try {
      const ingredientTranslations = await translateToRuAndEn(ingredientHy);
      setFormData(prev => ({
        ...prev,
        ingridientsDto: [{
          ...prev.ingridientsDto[0] || {},
          ingredientHy: ingredientHy,
          ingredientRu: ingredientTranslations.ru,
          ingredientEn: ingredientTranslations.en,
        }]
      }));
      toastRef.current?.addToast(
        getTranslation('toast.translationSuccess', language) || "Translation completed successfully!",
        "success"
      );
    } catch (error) {
      console.error("Translation error:", error);
      toastRef.current?.addToast(
        getTranslation('toast.translationFailed', language) || "Failed to translate. Please try again.",
        "error"
      );
    } finally {
      setIsTranslating(false);
    }
  }, [formData.ingridientsDto, language, toastRef]);

  // Translate all handler
  const handleTranslateAll = useCallback(async () => {
    setIsTranslating(true);
    try {
      // Translate dish name
      if (formData.nameHy && formData.nameHy.trim()) {
        const nameTranslations = await translateToRuAndEn(formData.nameHy);
        setFormData(prev => ({
          ...prev,
          nameRu: nameTranslations.ru,
          nameEn: nameTranslations.en,
        }));
      }

      // Translate portion
      if (formData.portionHy && formData.portionHy.trim()) {
        const portionTranslations = await translateToRuAndEn(formData.portionHy);
        setFormData(prev => ({
          ...prev,
          portionRu: portionTranslations.ru,
          portionEn: portionTranslations.en,
        }));
      }

      // Translate ingredients
      const ingredientHy = formData.ingridientsDto[0]?.ingredientHy;
      if (ingredientHy && ingredientHy.trim()) {
        const ingredientTranslations = await translateToRuAndEn(ingredientHy);
        setFormData(prev => ({
          ...prev,
          ingridientsDto: [{
            ...prev.ingridientsDto[0] || {},
            ingredientHy: ingredientHy,
            ingredientRu: ingredientTranslations.ru,
            ingredientEn: ingredientTranslations.en,
          }]
        }));
      }

      // Translate additions
      const additionsToTranslate = formData.dishAdditionDtoList
        .filter(add => add.nameHy && add.nameHy.trim())
        .map(add => add.nameHy);

      if (additionsToTranslate.length > 0) {
        const additionTranslations = await translateMultiple(additionsToTranslate);
        
        setFormData(prev => {
          const updatedAdditions = [...prev.dishAdditionDtoList];
          let translationIndex = 0;
          
          updatedAdditions.forEach((add, index) => {
            if (add.nameHy && add.nameHy.trim()) {
              updatedAdditions[index] = {
                ...add,
                nameRu: additionTranslations[translationIndex].ru,
                nameEn: additionTranslations[translationIndex].en,
              };
              translationIndex++;
            }
          });
          
          return {
            ...prev,
            dishAdditionDtoList: updatedAdditions,
          };
        });
      }

      toastRef.current?.addToast(
        getTranslation('toast.translationSuccess', language) || "Translation completed successfully!",
        "success"
      );
    } catch (error) {
      console.error("Translation error:", error);
      toastRef.current?.addToast(
        getTranslation('toast.translationFailed', language) || "Failed to translate. Please try again.",
        "error"
      );
    } finally {
      setIsTranslating(false);
    }
  }, [formData.nameHy, formData.portionHy, formData.ingridientsDto, formData.dishAdditionDtoList, language, toastRef]);

  // Add group configuration handler
  const handleAddGroupConfiguration = useCallback((selectedGroupOption) => {
    if (!selectedGroupOption?.value) return;

    const groupId = selectedGroupOption.value;

    if (!configuredGroupIds.includes(groupId)) {
      setConfiguredGroupIds(prev => [...prev, groupId]);
    }
  }, [configuredGroupIds]);

  // Remove group configuration handler
  const handleRemoveGroupConfiguration = useCallback((groupIdToRemove) => {
    // No longer enabling/disabling price field based on required groups

    // Remove the group ID from tracking
    setConfiguredGroupIds(prev => prev.filter(id => id !== groupIdToRemove));

    // Remove associated additions from formData
    setFormData(prev => ({
      ...prev,
      dishAdditionDtoList: prev.dishAdditionDtoList.filter(add => add.group?.id !== groupIdToRemove)
    }));

    // Clear any validation errors associated with this group
    const errorKey = `additionGroup_${groupIdToRemove}`;
    if (errors[errorKey]) {
      setErrors(prevErrs => ({...prevErrs, [errorKey]: undefined}));
    }
  }, [errors]);

  // Create new addition handler
  const handleCreateNewAddition = useCallback(async (newAdditionData) => {
    try {
      // Check if an addition with the same name already exists in the group
      const existingAddition = allDishAdditions.find(add =>
        add.nameHy === newAdditionData.nameHy &&
        add.group?.id === newAdditionData.group.id
      );

      let newAddition = null;

      if (existingAddition) {
        // Use the existing addition
        newAddition = existingAddition;
      } else {
        // Create a new addition
        // Validate price - only allow digits (0-9)
        if (!isValidPrice(newAdditionData.price)) {
          throw new Error("Price must contain only digits (0-9)");
        }

        // Prepare the addition data for the API
        const additionToSave = {
          nameHy: newAdditionData.nameHy,
          price: newAdditionData.price === '' ? 0 : parseFloat(newAdditionData.price),
          group: newAdditionData.group || null,
          nameEn: '',
          nameRu: ''
        };

        // Save to backend
        await saveDishAddition(additionToSave);

        // Refresh the additions list from the backend
        const updatedAdditions = await fetchDishAdditions();
        setAllDishAdditions(updatedAdditions || []);

        // Find the newly created addition
        newAddition = updatedAdditions.find(add =>
          add.nameHy === newAdditionData.nameHy &&
          add.group?.id === newAdditionData.group.id
        );
      }

      if (newAddition) {
        // Get existing selected additions for this group
        const existingGroupAdditions = formData.dishAdditionDtoList.filter(add =>
          add.group?.id === newAdditionData.group.id
        );

        // Format the new addition as a selection option
        const newOption = {
          value: newAddition.id,
          label: newAddition.nameHy,
          addition: newAddition
        };

        // Format existing selections as options
        const existingOptions = existingGroupAdditions.map(add => ({
          value: add.id,
          label: add.nameHy,
          addition: add
        }));

        // Combine existing options with the new option
        // But first check if the addition is already selected
        const isAlreadySelected = existingOptions.some(opt => opt.value === newAddition.id);
        const allOptions = isAlreadySelected
          ? existingOptions
          : [...existingOptions, newOption];

        // Update selections with the combined list
        handleGroupedAdditionChange(allOptions, newAddition.group);
      }

      // Show success message
      const messageKey = existingAddition ? 'menu.additionAddedSuccess' : 'menu.additionCreatedSuccess';
      toastRef.current?.addToast(
        getTranslation(messageKey, language) || 
        (existingAddition ? "Addition added successfully" : "Addition created successfully"),
        "success"
      );
    } catch (error) {
      console.error("Error creating new addition:", error);
      toastRef.current?.addToast(
        getTranslation('menu.additionCreationFailed', language) || 
        "Failed to create addition",
        "error"
      );
    }
  }, [allDishAdditions, formData.dishAdditionDtoList, handleGroupedAdditionChange, language, toastRef]);

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = validateDishForm({...formData, image}, language);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, image, language]);

  // Form submission
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return; // Prevent multiple submissions

    if (!validateForm()) {
      toastRef.current?.addToast(
        getTranslation('menu.formValidationError', language) || 
        "Please fill in all required fields", 
        "error"
      );

      // Simple scroll to top as a fallback
      window.scrollTo({top: 0, behavior: 'smooth'});
      return;
    }

    try {
      setIsSubmitting(true);

      // Clean up additions data
      const cleanedFormData = cleanFormDataForSubmission(formData);

      // Transform data for backend
      const transformedData = TransformDataForBackend(cleanedFormData);

      if (dishData?.id) {
        await updateDish(dishData.id, transformedData, image);
        const successMessage = getTranslation('menu.dishUpdatedSuccess', language) || 
          "Dish updated successfully";
        toastRef.current?.addToast(successMessage, "success");
        
        // Small delay to ensure toast is visible before navigation
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        await createDish(formData.chefId, transformedData, image);
        const successMessage = getTranslation('menu.dishCreatedSuccess', language) || 
          "Dish created successfully";
        toastRef.current?.addToast(successMessage, "success");
        
        // Small delay to ensure toast is visible before navigation
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      onDishCreated(); // Callback to parent component
    } catch (error) {
      console.error("Error submitting dish:", error);
      toastRef.current?.addToast(
        getTranslation('common.error', language) || 
        "An error occurred, please try again later", 
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [dishData?.id, formData, image, isSubmitting, language, onDishCreated, toastRef, validateForm]);

  return {
    // State
    formData,
    errors,
    isSubmitting,
    image,
    isTranslating,
    additionGroups,
    configuredGroupIds,
    groupedAdditionsMap,
    ungroupedAdditionOptions,
    selectedUngroupedValues,
    availableGroupsForConfig,

    // Handlers
    setImage,
    setErrors,
    handleInputChange,
    handleSingleSelectChange,
    handleMultiSelectChange,
    handleUngroupedAdditionsChange,
    handleGroupedAdditionChange,
    handleAdditionPriceOverrideChange,
    handleTranslationChange,
    handleTranslateIngredients,
    handleTranslateAll,
    handleAddGroupConfiguration,
    handleRemoveGroupConfiguration,
    handleCreateNewAddition,
    handleSubmit,

    // Validation
    validateForm
  };
};

export default useDishForm;
